import * as selectors from './selectors'
import { getLastSelection } from './selectors'
import { builtInAPIUrl, builtInHybridUrl, builtInAppUrl, builtInRTFUrl } from '../Sidebar/Urls'
import { getApps } from '../../remote-api/MergeRemoteApi'
import { ResourceAPISelected, ResourceAppSelected, ResourceSelected, ResourceSelectorContainer, ResourcesState } from './model'
import { API, APIDetail, Application, ARMApplication, ARMApplicationDetail, ARMHybridDetail, RTFApplication } from '../../remote-api/models'
import { Dispatch, ExtraArgs, GetState } from '../../types'
import { changePlatformToken, selectors as authSelectors } from '../../lifecycle/auth'
import { ResourceSelectorService } from './util/ResourceSelectorService'
import { Environment } from '../../remote-api/CoreServicesRemoteApi'
import ARMRemoteApi from '../../remote-api/ARMRemoteApi'
import { isRTF } from '../BuiltInDashboard/common/hybrid'

/**
 *  Action Constants
 */
export enum ResourcesValues {
  Clear = 'Monitoring/Environment/Clear',
  ChangeEnv = 'Monitoring/Environment/ChangeEnvironments',
  LoadEnv = 'Monitoring/Environment/LoadingEnvironments',
  SelectEnv = 'Monitoring/Environment/SelectEnv',
  ChangeApp = 'Monitoring/Application/ChangeApps',
  LoadApp = 'Monitoring/Application/LoadingApps',
  SelectApp = 'Monitoring/Application/SelectApp',
  ChangeApi = 'Monitoring/Api/ChangeApi',
  LoadApi = 'Monitoring/Api/LoadApi',
  SelectApi = 'Monitoring/Api/SelectApi',
  SelectAPIVersionId = 'Monitoring/Api/SelectAPIVersionId',
  ResourceDetailsLoaded = 'Monitoring/Application/ResourceDetailsLoaded',
  SelectDashboardType = 'Monitoring/DashboardType',
  ChangeSelectedResourceType = 'Monitoring/ChangeSelectedResourceType',
  ChangeActiveLandingPage = 'Monitoring/ChangeActiveLandingPage',
  HandleOpenModal = 'Monitoring/ResourceSelector/HandleOpenModal',
  HandleCloseModal = 'Monitoring/ResourceSelector/HandleCloseModal',
  SaveResourceSelector = 'Monitoring/ResourceSelector/SaveResourceSelector',
}

/**
 *  Action Type
 */
export type ActiveLandingPageAction = {
  type: ResourcesValues.ChangeActiveLandingPage,
  payload: boolean
}

export type ClearAction = {
  type: ResourcesValues.Clear
}
export type ChangeEnvironmentAction = {
  type: ResourcesValues.ChangeEnv
  payload: Environment[]
}
export type LoadingEnvironmentAction = {
  type: ResourcesValues.LoadEnv
}
export type ChangeApplicationAction = {
  type: ResourcesValues.ChangeApp
  payload: Application[]
}
export type LoadingApplicationAction = {
  type: ResourcesValues.LoadApp
}
export type SelectApplicationAction = {
  type: ResourcesValues.SelectApp
  payload: { appId: string, targetId: number | undefined }
}
export type ResourceDetailsLoadedAction = {
  type: ResourcesValues.ResourceDetailsLoaded
  payload?: ARMApplicationDetail | ARMApplication | APIDetail | RTFApplication,
}
export type SelectEnvironmentAction = {
  type: ResourcesValues.SelectEnv
  payload: string
}
export type ChangeApiAction = {
  type: ResourcesValues.ChangeApi
  payload: { data: API[], loading: boolean }
}
export type LoadingApiAction = {
  type: ResourcesValues.LoadApi
}
export type SelectApiAction = {
  type: ResourcesValues.SelectApi
  payload: string | number
}
export type SelectAPIVersionIdAction = {
  type: ResourcesValues.SelectAPIVersionId
  payload: string | number
}

export type SelectTypeAction = {
  type: ResourcesValues.SelectDashboardType
  payload: string | number
}
export type ChangeSelectedResourceTypeAction = {
  type: ResourcesValues.ChangeSelectedResourceType
  payload: string
}

export type HandleOpenModalAction = {
  type: 'Monitoring/ResourceSelector/HandleOpenModal'
}

export type HandleCloseModalAction = {
  type: 'Monitoring/ResourceSelector/HandleCloseModal'
}

export type SaveResourceSelectorAction = {
  type: ResourcesValues.SaveResourceSelector
  payload: ResourceSelected
}

/**
 *  Action Creators
 */
const changeActiveLandingPage = (isActive: boolean): ActiveLandingPageAction =>
  ({type: ResourcesValues.ChangeActiveLandingPage, payload: isActive})

export const clearEnviroments = (): ClearAction =>
  ({type: ResourcesValues.Clear})

const changeEnvironment = (env: Environment[]): ChangeEnvironmentAction =>
  ({type: ResourcesValues.ChangeEnv, payload: env})

const loadingEnvironment = (): LoadingEnvironmentAction =>
  ({type: ResourcesValues.LoadEnv})

const changeApplication = (apps: Application[]): ChangeApplicationAction =>
  ({type: ResourcesValues.ChangeApp, payload: apps})

const loadingApplication = (): LoadingApplicationAction =>
  ({type: ResourcesValues.LoadApp})

const selectEnvironment = (envId: string): SelectEnvironmentAction =>
  ({type: ResourcesValues.SelectEnv, payload: envId})

const selectApplication = (appId: string, targetId?: number): SelectApplicationAction =>
  ({type: ResourcesValues.SelectApp, payload: {appId: appId, targetId: targetId}})

const loadResourceDetails = (detail?: ARMApplicationDetail | ARMApplication | APIDetail | RTFApplication): ResourceDetailsLoadedAction =>
  ({type: ResourcesValues.ResourceDetailsLoaded, payload: detail})

const selectApi = (apiId: string): SelectApiAction =>
  ({type: ResourcesValues.SelectApi, payload: apiId})

const selectVersion = (versionId: string | number): SelectAPIVersionIdAction =>
  ({type: ResourcesValues.SelectAPIVersionId, payload: versionId})

const selectDashboardType = (dashboardType: string | number): SelectTypeAction =>
  ({type: ResourcesValues.SelectDashboardType, payload: dashboardType})

const loadingApi = (): LoadingApiAction =>
  ({type: ResourcesValues.LoadApi})

const changeApi = (api: API[], loading?: boolean): ChangeApiAction =>
  ({type: ResourcesValues.ChangeApi, payload: {data: api, loading: loading ? loading : true}})

const changeSelectedResourceType = (type: string): ChangeSelectedResourceTypeAction =>
  ({type: ResourcesValues.ChangeSelectedResourceType, payload: type})

const handleOpenModal = (): HandleOpenModalAction => ({
  type: ResourcesValues.HandleOpenModal
})

const handleCloseModal = (): HandleCloseModalAction => ({
  type: ResourcesValues.HandleCloseModal
})

const saveResourceSelector = (rs: ResourceSelected): SaveResourceSelectorAction => ({
  type: ResourcesValues.SaveResourceSelector,
  payload: rs
})

const UNDEPLOYED = 'UNDEPLOYED'

export const handleOpen = () => (dispatch: Dispatch) => {
  dispatch(undoSelection())
  dispatch(handleOpenModal())
}

export const closeModalSelector = () => (dispatch: Dispatch) => {
  dispatch(handleCloseModal())
}

export const getBuiltinFromLocalStorage =
  (): { env?: string, app?: string, dashboard?: string, resourceType?: string | undefined, hiddenDetail?: boolean } => {
    const item = localStorage.getItem('builtin')
    if (!item) return {}
    try {
      return JSON.parse(item)
    } catch (e) {
      return {}
    }
  }

export const undoSelection = () => (dispatch: Dispatch, getState: GetState) => {
  dispatch(handleCloseModal())
  const ls = getLastSelection(getState())
  if (ls.apiId && ls.versionId) {
    if (ls.apiData) dispatch(changeApi(ls.apiData))
    if (ls.resourceType) dispatch(changeSelectedResourceType(ls.resourceType))
    if (ls.environment) dispatch(selectEnvironment(ls.environment))
    if (ls.apiId) dispatch(selectApi(ls.apiId))
    if (ls.apiId && ls.environment && ls.versionId && ls.dashboardType) {
      dispatch(selectVersions(ls.environment, ls.apiId, ls.versionId))
    }
  } else if (ls.application && ls.environment && ls.dashboardType) {
    if (ls.appData) dispatch(changeApplication(ls.appData))
    if (ls.resourceType) dispatch(changeSelectedResourceType(ls.resourceType))
    if (ls.environment) dispatch(selectEnvironment(ls.environment))
    dispatch(selectApplications(ls.environment, ls.application, ls.resourceType, ls.targetId))
  }
}

export const fetchEnvironments = (rsc: ResourceSelectorContainer) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(loadingEnvironment)
    const {orgId} = rsc

    return api.cs(getState)
      .fetchEnvironments(orgId).then((env: ResourcesState['environments']) => {
        dispatch(changeEnvironment(env.data))
        const selectedEnv = rsc.envId || getBuiltinFromLocalStorage().env
        const existingEnvId = env.data.map(e => e.id).find(id => id === selectedEnv)
        const id = existingEnvId || authSelectors.getPreferredEnvironment(getState(), orgId)
        if (id) dispatch(selectEnvironments({...rsc, envId: id}))
      })
  }

const fetchApplications =
  (resourceSelectorContainer: ResourceSelectorContainer) =>
    (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
      dispatch(loadingApplication())

      const {orgId, envId, appId, dashboardType, isLogFiles} = resourceSelectorContainer
      const environment = envId ? envId : ''
      const remoteApi: ARMRemoteApi = api.arm(getState)
      const resourceSelectorService = new ResourceSelectorService(remoteApi)

      return getApps(orgId, environment, getState, {api}, false).then((apps: Application[]) => {
        const selectedApp = isLogFiles ? '' : appId || getBuiltinFromLocalStorage().app
        const selectedDashboard = getBuiltinFromLocalStorage().dashboard || dashboardType
        const cloudhubApps = apps.filter(app => app.targetType === 'app')
        const rtfApps = apps.filter(app => isRTF(app.targetType))
        const otherApps = apps.filter(app => !isRTF(app.targetType) && app.targetType !== 'app')

        Promise.all(
          [
            resourceSelectorService.fetchCloudhubApplications(orgId, environment, cloudhubApps),
            resourceSelectorService.fetchRTFApplications(orgId, environment, rtfApps),
            resourceSelectorService.fetchHybridApplications(orgId, environment, otherApps)
          ])
          .then(results => {
            const finalApps: Application[] = results[0].concat(results[1]).concat(results[2])

            dispatch(changeApplication(finalApps))

            const existingAppId = finalApps.map(e => {
              return {id: e.id, rsType: e.targetType, targetId: e.targetId, serverName: e.serverName, muleVersion: e.muleVersion}
            }).find(app => app.id === selectedApp)
            // pre-select an App id
            if (existingAppId && selectedDashboard) {
              const rs = {
                environment: environment,
                application: existingAppId.id,
                dashboardType: selectedDashboard,
                resourceType: existingAppId.rsType,
                targetId: existingAppId.targetId,
                serverName: existingAppId.serverName,
                muleVersion: existingAppId.muleVersion
              }
              dispatch(selectApplicationsAndStored(rs))
            }
          }).catch((error) => {
          dispatch(changeApplication(apps))
        })
      }).catch((error: any) => {
        console.error(error)
        const environment = envId ? envId : ''
        const dashboard = getBuiltinFromLocalStorage().dashboard
        const selectedApp = isLogFiles && dashboard && dashboard.indexOf('http.analytics') > -1 ? '' :
          appId || getBuiltinFromLocalStorage().app
        return api.arm(getState)
          .fetchApplications(orgId, environment)
          .then((app: { data: ARMApplication[] }) => {
            const apps = app.data
              .filter(app => app.lastReportedStatus !== UNDEPLOYED)
              .map(app => {
                return {
                  id: app.details.domain || app.artifact.name,
                  status: app.lastReportedStatus,
                  targetType: app.target.type,
                  targetId: app.target.id,
                  serverName: app.serverName
                }
              })
            const existingAppId = apps.find(app => app.id === selectedApp)
            dispatch(changeApplication(apps))
            const appSelected = apps.find(a => a.id === selectedApp)
            // pre-select an App id
            if (selectedApp && dashboardType) {
              const rs = {
                environment: environment,
                application: selectedApp,
                dashboardType: dashboardType,
                resourceType: existingAppId ? existingAppId.targetType.toLowerCase() : '',
                targetId: appSelected ? appSelected.targetId : 0,
                serverName: existingAppId ? existingAppId.serverName : ''
              }
              dispatch(selectApplicationsAndStored(rs))
            }
          })
      })
    }

const transformAPIs = (assets: any[]): API[] => {
  return assets.map((asset) =>
    (
      asset.apis.map(
        (api: any) => ({...api, id: `${api.id}`, assetId: asset.id, exchangeAssetName: asset.exchangeAssetName})
      )
    )
  )
}

export const fetchApis = (orgId: string, envId: string, apiId?: string | number, dashboardType?: string | number) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<any> => {
    dispatch(loadingApi())
    if (!authSelectors.getPlatformToken(getState())) {
      return api.apiPlatform(getState).getAPIPlatformToken().then(
        (session) => {
          dispatch(changePlatformToken(session.token))
          return dispatch(fetchApis(orgId, envId))
        }
      ).catch((error: any) => {
        console.error(error)
        dispatch(changeApi([] as API[]))
      })
    } else {
      return api.apiManager(getState)
        .fetchAPIs(orgId, envId, {limit: 100, offset: 0}).then((response) => {
          const totalAssets = response.total
          let assets = response.assets
          const assetsLength = assets.length

          let apis = transformAPIs(assets)
          dispatch(changeApi(apis))

          if (totalAssets > assetsLength) {
            const offsets = totalAssets / assetsLength

            for (let i = 1; i < offsets; i++) {
              api.apiManager(getState).fetchAPIs(orgId, envId, {limit: 100, offset: i * 100}).then((offsetResponse) => {
                assets = assets.concat(offsetResponse.assets)
                apis = transformAPIs(assets)

                let loading = i + 1 < offsets
                dispatch(changeApi(apis, loading))
              })
            }
          }

          const selectedApi = apiId || getBuiltinFromLocalStorage().app
          const selectedDashboard = getBuiltinFromLocalStorage().dashboard || dashboardType
          const existingApiId = apis
            .map(e => {
              return {value: e.id, assetId: `${e.assetId}`}
            })
            .find(a => a.value === selectedApi)
          // pre-select an Api id

          if (existingApiId && selectedDashboard) {
            dispatch(selectApis(envId, existingApiId.assetId))
            if (selectedApi) {
              const rs = {
                environment: envId,
                apiId: existingApiId.assetId,
                versionId: existingApiId.value,
                dashboardType: existingApiId.value,
                targetType: 'api',
                resourceType: 'api'
              }
              dispatch(selectVersionsStoredAndFetchDetails(rs))
            }
          }
        }).catch((error: any) => {
          console.error(error)
          dispatch(changeApi([] as API[]))
        })
    }
  }

export const selectEnvironments = (rsc: ResourceSelectorContainer) => (dispatch: Dispatch) => {
  const {orgId, envId, resourceId, dashboardType, isLogFiles} = rsc
  const resolvedEnvId = envId ? envId : ''
  dispatch(selectEnvironment(resolvedEnvId))
  dispatch(fetchApplications(rsc))
  if (!isLogFiles) {
    dispatch(fetchApis(orgId, resolvedEnvId, resourceId, dashboardType))
  }
}

export const fetchDetails = () => (dispatch: Dispatch, getState: GetState) => {
  const orgId = authSelectors.getOrganizationId(getState())
  const envId = selectors.getSelectedEnvironmentId(getState())
  const appId = selectors.getSelectedApplicationId(getState())
  if (!orgId || !envId) return

  if (appId) {
    dispatch(fetchApplicationDetails(orgId, envId, appId))
  } else {
    const apiId = selectors.getSelectedAPIVersion(getState())
    if (apiId)
      dispatch(fetchApiDetails(orgId, envId, apiId))
  }
}

const ENDPOINT_STATUS = {
  active: 'active',
  inactive: 'inactive',
  unregistered: 'unregistered'
}

const inactivityThreshold = 300

export const fetchApiDetails = (orgId: string, envId: string, id: string | number) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<any> => {
    if (!authSelectors.getPlatformToken(getState())) {
      return api.apiPlatform(getState).getAPIPlatformToken().then(
        (session) => {
          dispatch(changePlatformToken(session.token))
          return dispatch(fetchApiDetails(orgId, envId, id))
        }
      ).catch((error: any) => {
        console.error(error)
      })
    } else {
      return api.apiManager(getState)
        .fetchAPI(orgId, envId, id.toString()).then((apiDetail) => {
          if (apiDetail.deployment && apiDetail.deployment.id) {
            api.xapiProxy(getState).fetchStatus(orgId, envId, id, apiDetail.deployment.id).then((apiStatus) => {
              dispatch(loadResourceDetails({...apiDetail, apiStatus: apiStatus}))
            })
          } else {
            let status = ''
            if (!apiDetail.lastActiveDate) {
              status = ENDPOINT_STATUS.unregistered
            } else {
              const activeDelta = apiDetail.lastActiveDelta || inactivityThreshold + 1
              status = activeDelta > inactivityThreshold ? ENDPOINT_STATUS.inactive : ENDPOINT_STATUS.active
            }
            dispatch(loadResourceDetails({
              ...apiDetail, apiStatus: {
                fullDomain: '',
                lastActiveDate: apiDetail.lastActiveDate || '',
                status: status
              }
            }))
          }
        })
    }
  }

const hybridTargetTypes = ['SERVER', 'SERVER_GROUP', 'CLUSTER']
const rtfTargetType = 'MC'
const isTargetHybrid = (targetType: string) => (hybridTargetTypes.indexOf(targetType) > -1)

export const fetchApplicationDetails = (orgId: string, envId: string, id: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    const appName = id.substring(0, id.indexOf('.'))
    if (appName) {
      api.arm(getState)
        .fetchApplicationData(orgId, envId, appName)
        .then(details => {
          dispatch(loadResourceDetails(details))
        }).catch(() => {
        dispatch(loadResourceDetails(undefined))
      })
    } else {
      api.arm(getState)
        .fetchApplications(orgId, envId)
        .then((app: { data: ARMApplication[] }) => {
          const found: ARMApplication | RTFApplication | undefined = app.data.find(auxApp => auxApp.id === id || auxApp.artifact.name === id)
          if (found && isTargetHybrid(found.target.type)) {
            api.arm(getState)
              .fetchHybridData(orgId, envId, found.id)
              .then((hybridApp: { data: ARMHybridDetail }) => {
                found.gatewayVersion = hybridApp.data.target.gatewayVersion
                dispatch(loadResourceDetails(found))
              }).catch(() => {
              dispatch(loadResourceDetails(undefined))
            })
          } else if (found && found.target.type === rtfTargetType) {
            api.arm(getState)
              .fetchRTFData(orgId, envId, found.id)
              .then((rtfAPP: RTFApplication) => {
                dispatch(loadResourceDetails(rtfAPP))
              }).catch(() => {
              dispatch(loadResourceDetails(found))
            })
          } else {
            dispatch(loadResourceDetails(found))
          }
        }).catch(() => {
        dispatch(loadResourceDetails(undefined))
      })
    }
  }

export const selectApplicationsAndStored = (ras: ResourceAppSelected) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(selectApplication(ras.application, ras.targetId))
  dispatch(selectDashboardType(ras.dashboardType))
  dispatch(changeSelectedResourceType(ras.resourceType))
  const rs: ResourceSelected = {
    environment: ras.environment,
    application: ras.application,
    apiId: '',
    versionId: '',
    dashboardType: ras.dashboardType,
    resourceType: ras.resourceType,
    targetId: ras.targetId,
    serverName: ras.serverName,
    appData: selectors.getApplications(getState()),
    muleVersion: ras.muleVersion
  }
  dispatch(saveResourceSelector(rs))
  dispatch(fetchDetails())
}

export const selectApplications =
  (envId: string, id: string, resourceType: string, targetId: number | undefined) => (dispatch: Dispatch) => {
    dispatch(selectApplication(id, targetId))
    dispatch(changeSelectedResourceType(resourceType))
  }

export const selectApis = (envId: string, id: string) => (dispatch: Dispatch) => {
  dispatch(selectApi(id))
  dispatch(changeSelectedResourceType('api'))
}

export const selectVersions = (envId: string, apiId: string, version: string | number) => {
  return (dispatch: Dispatch) => {
    dispatch(selectVersion(version))
  }
}

const analyticsDashboardType = 'http.analytics'

export const selectVersionsStoredAndFetchDetails = (ras: ResourceAPISelected) =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(selectVersion(ras.versionId))
    dispatch(changeSelectedResourceType(ras.resourceType))
    const rs: ResourceSelected = {
      environment: ras.environment,
      application: '',
      apiId: ras.apiId,
      versionId: ras.versionId,
      dashboardType: analyticsDashboardType,
      resourceType: ras.resourceType,
      apiData: selectors.getApis(getState())
    }
    dispatch(saveResourceSelector(rs))
    dispatch(fetchDetails())
  }

export const saveAppSelected = (envId: string, id: string, dashboardType: string, resourceType: string, targetId: number | undefined) =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(fetchDetails())

    const appFound = selectors.getApplications(getState()).find(a => a.id === id)
    const rs: ResourceSelected = {
      environment: envId,
      application: id,
      apiId: '',
      versionId: '',
      dashboardType: dashboardType,
      resourceType: resourceType,
      appData: selectors.getApplications(getState()),
      serverName: appFound && appFound.targetType === 'server' ? appFound.serverName : undefined,
      targetId: targetId,
      muleVersion: appFound ? appFound.muleVersion : undefined
    }
    dispatch(saveResourceSelector(rs))

    switch (resourceType) {
      case 'app':
        window.location.hash = builtInAppUrl(envId, id, dashboardType)
        break
      case 'mc':
        window.location.hash = builtInRTFUrl(envId, id, dashboardType)
        break
      case 'cluster':
      case 'server_group':
      case 'server':
        window.location.hash = targetId ? builtInHybridUrl(envId, id, targetId, dashboardType, resourceType) : 'missing-resource'
        break
      default:
        window.location.hash = builtInAppUrl(envId, id, dashboardType)
    }
    dispatch(changeLandingPage(false))
    dispatch(handleCloseModal())

    updateLocalStorage(envId, id, dashboardType, false)
  }

export const saveApiSelected = (envId: string, apiId: string, versionId: string, dashboardType: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(fetchDetails())

    window.location.hash = builtInAPIUrl(envId, apiId, versionId, dashboardType)

    dispatch(changeLandingPage(false))
    dispatch(handleCloseModal())

    const rs: ResourceSelected = {
      environment: envId,
      application: '',
      apiId: apiId,
      versionId: versionId,
      dashboardType: dashboardType,
      resourceType: 'api',
      apiData: selectors.getApis(getState())
    }

    dispatch(saveResourceSelector(rs))
    updateLocalStorage(envId, versionId, dashboardType, false)
  }

export const changeLandingPage = (isActive: boolean) => (dispatch: Dispatch) => {
  dispatch(changeActiveLandingPage(isActive))
}

export const selectDashboardTypes = (dashboardType: string) => (dispatch: Dispatch) => {
  dispatch(selectDashboardType(dashboardType))
  const builtin = getBuiltinFromLocalStorage()
  updateLocalStorage(builtin.env, builtin.app, dashboardType, builtin.hiddenDetail)
}

export const changeSelectedApplication = (id: string) => (dispatch: Dispatch) => {
  dispatch(selectApplication(id))
}

export const updateLocalStorage = (envId: string | undefined, id: string | undefined, dashboardType: string, hiddenDetail?: boolean) => {
  localStorage.setItem(
    'builtin',
    JSON.stringify(
      {
        env: envId,
        app: id,
        dashboard: dashboardType,
        hiddenDetail: hiddenDetail
      }
    )
  )
}

export type ResourcesSelectorActions =
  | ClearAction
  | ChangeEnvironmentAction
  | LoadingEnvironmentAction
  | ChangeApplicationAction
  | LoadingApplicationAction
  | SelectApplicationAction
  | ResourceDetailsLoadedAction
  | SelectEnvironmentAction
  | SelectAPIVersionIdAction
  | ChangeApiAction
  | LoadingApiAction
  | SelectApiAction
  | SelectTypeAction
  | ChangeSelectedResourceTypeAction
  | ActiveLandingPageAction
  | HandleOpenModalAction
  | HandleCloseModalAction
  | SaveResourceSelectorAction
